import { Injectable } from '@angular/core';
import { HttpService } from '../http/http.service';
import { Resource } from './interface/resource';
import { CookieService } from '../../service/cookie.service';
import { interval, map, Observable, Subject, takeUntil, tap, zip } from 'rxjs';
import { Language } from './interface/language';
import { ResourceRequest } from '../resource-manager/interface/create-resource-request.model';
import { StaticResource } from './interface/static-resource';
import { MatDialog } from '@angular/material/dialog';
import { LiveResourceDialogComponent } from './component/live-dialog-resource/live-resource-dialog.component';

const COPYWRITER = 'copywriter';

@Injectable({
  providedIn: 'root'
})
export class I18nService {
  languages: Language[] = [];
  translatedQueue: string[] = [];

  private apiPath = '/api/resource/';
  private resources: Resource[] = [];
  private untranslatedQueue: string[] = [];
  private translationStarted = false;
  private deactivateCopywriter?: Subject<void>;

  constructor(private httpSvc: HttpService,
              private dialog: MatDialog) {
    if (this.copywriterMode) {
      this.startCopywriterInterval();
    }
  }

  get copywriterMode(): boolean {
    return JSON.parse(localStorage.getItem(COPYWRITER) ?? 'false') ?? false;
  }

  set copywriterMode(mode: boolean) {
    localStorage.setItem(COPYWRITER, JSON.stringify(mode));
    if (mode) {
      this.startCopywriterInterval();
    } else {
      this.deactivateCopywriter?.next();
      this.deactivateCopywriter?.complete();
    }
  }

  private _selLang: Language | undefined;

  get selLang(): Language | undefined {
    return this._selLang;
  }

  set selLang(lang: Language | undefined) {
    this._selLang = lang;
    if (this._selLang) {
      CookieService.set('lang', this._selLang.code);
    }
  }

  /**
   * Static get language code method
   */
  static getLang(): string {
    return CookieService.get('lang') ?? 'en-US';
  }

  getAllResources(): Observable<Resource[]> {
    return this.httpSvc.get(`${this.apiPath}all-resources`).pipe(
      map(resources => {
        return (<Resource[]>resources)
          .sort((a, b) => b.id - a.id);
      })
    );
  }

  getAllLanguages(): Observable<Language[]> {
    return this.httpSvc.get(`${this.apiPath}all-languages`);
  }

  /**
   * Returns resources from file
   * @param path
   */
  getStaticResources(path: string): Observable<StaticResource> {
    return this.httpSvc.get(path);
  }

  createResource(request: ResourceRequest): Observable<boolean> {
    return this.httpSvc.post(`${this.apiPath}new-resource`, request).pipe(
      map(result => result?.success)
    );
  }

  updateResource(request: ResourceRequest): Observable<any> {
    return this.httpSvc.put(`${this.apiPath}update-resource`, request);
  }

  deleteResource(res: Resource): Observable<any> {
    return this.httpSvc.delete(`${this.apiPath}${res.id}`);
  }

  /**
   * Load resources from server
   */
  loadResources(paths?: string[]): Observable<any> {
    if (paths) {
      // load static json file from server
      return zip(paths.map(path => this.getStaticResources(path)))
        .pipe(
          tap(staticResources => {
            this.resources = [];
            this.languages = [];
            staticResources.forEach(res => {
              this.resources.push(...res.resources.map((r, index) => {
                return {
                  id: index,
                  resourceKey: r.resourceKey,
                  text: r.text,
                  languageID: res.language
                };
              }));
              this.languages.push(res.language);
            });
            this.getLanguage();
          })
        );
    } else {
      // load from resource controller
      return zip([this.getAllResources(), this.getAllLanguages()])
        .pipe(
          tap(result => {
            this.resources = result[0];
            this.languages = result[1];
            this.getLanguage();
          }));
    }
  }

  /**
   * Returns translated or original text
   * @param val
   * @param args optional arguments to replace {0}, {1}, etc..
   */
  translate(val: string, ...args: string[]): string {
    if (!val || val === '') return val;
    val = val.trim();
    if (val.startsWith('#')) {
      return val.substring(1);
    } else {
      const resourceText = JSON.parse(JSON.stringify(this.resources
        .find(r => r.resourceKey === val && r.languageID.code === this._selLang?.code) ?? ''));
      if (resourceText && args) {
        args.forEach((arg, index) => {
          resourceText.text = resourceText.text?.replace(`{${index}}`, arg);
        });
      }
      if (!resourceText && this.copywriterMode) {
        console.log(`i18n: missing translation '${val}'`);

        if (val.includes('.') && !this.untranslatedQueue.includes(val) &&
          !this.translatedQueue.includes(val)) {
          this.untranslatedQueue.push(val);
        }
      }
      return resourceText?.text ?? val;
    }
  }

  getPipeFn = (val: any): string => {
    return this.translate(val);
  };

  isReady() {
    return this.resources && this.resources.length > 0;
  }

  /**
   * Determines and sets the selected language for the application.
   * The method checks the language preference in the following order:
   * 1. From a 'lang' cookie, if available.
   * 2. From the browser's language preferences, if available.
   * 3. Defaults to the first language in the `languages` array if no match is found.
   *
   * @return {void} This method performs operations to determine and set the selected language
   * but does not return any value.
   */
  private getLanguage() {
    const cookieLang = CookieService.get('lang');
    if (cookieLang) {
      this.selLang = this.languages.find(l => l.code === cookieLang);
    } else if (navigator.languages.length > 0) {
      const foundLang = this.languages.find(l =>
        navigator.languages.some(l2 => l2.includes(l.code.slice(0, 2))));
      if (foundLang) {
        this.selLang = foundLang;
      }

    }
    if (!this.selLang) {
      this.selLang = this.languages[0];
    }
  }


  private checkToLiveTranslates() {
    if (this.untranslatedQueue.length === 0) {
      this.translationStarted = false;
      return;
    }

    const ref = this.dialog.open(LiveResourceDialogComponent, {
      hasBackdrop: false,
      data: {
        languages: this.languages,
        resourceKey: this.untranslatedQueue[0]
      }
    });

    ref.afterClosed().subscribe(() => {
      this.untranslatedQueue.shift();
      if (this.copywriterMode) {
        this.checkToLiveTranslates();
      } else {
        this.untranslatedQueue = [];
      }

    });
  }

  private startCopywriterInterval() {
    this.deactivateCopywriter = new Subject<void>();
    interval(3000)
      .pipe(takeUntil(this.deactivateCopywriter))
      .subscribe(_ => {
        if (!this.translationStarted) {
          this.translationStarted = true;
          this.checkToLiveTranslates();
        }
      });
  }
}
